import { createFormHook, useStore } from '@tanstack/react-form'
import { sameFormValue } from '@/lib/form-dirty'
import { Button } from '../ui/button'
import { useEffect, useId, useRef, useSyncExternalStore } from 'react'
import type React from 'react'
import { useBlocker } from '@tanstack/react-router'
import { fieldContext, formContext, useFormContext } from './form/shared'
import { cn } from '../../lib/utils'
import { Spinner } from '../ui/spinner'
import { useLingui } from '@lingui/react/macro'
import { useConfirm } from './confirm-dialog'
import { toast } from '../../lib/toast'

// Every field is a static import: the whole set is ~70 KB gzip in one chunk against ~46 KB of extra first
// load and twenty-two chunks if they are split, and the pages that mount a form pull it anyway. The landing
// is the exception and pays none of it — its contact form mounts on appear (landing/contact-section.tsx).
import TextField from './form/text-field'
import TextareaField from './form/textarea-field'
import SwitchField from './form/switch-field'
import PasswordField from './form/password-field'
import { CheckboxField } from './form/checkbox-field'
import ColorField from './form/color-field'
import ComboboxNumberField from './form/combobox-number-field'
import ComboboxTextField from './form/combobox-text-field'
import CountryField from './form/country-field'
import DateField from './form/date-field'
import DateTimeField from './form/datetime-field'
import FileField from './form/file-field'
import ImageField from './form/image-field'
import ImageUploadField from './form/image-upload-field'
import MultiComboboxNumberField from './form/multi-combobox-number-field'
import MultiComboboxPopoverTextField from './form/multi-combobox-popover-text-field'
import MultiComboboxTextField from './form/multi-combobox-text-field'
import ProvinceField from './form/province-field'
import NativeCountryField from './form/native-country-field'
import NativeProvinceField from './form/native-province-field'
import { FunctionField, SectorField } from './form/list-select-field'
import SelectNumberField from './form/select-number-field'
import SelectTextField from './form/select-text-field'
import SimpleSelectField from './form/simple-select-field'
import SimpleSelectNumberField from './form/simple-select-number-field'

export { fileToBase64 } from './form/file-to-base64'

function SubmitButton({
  label,
  icon,
  ...props
}: React.ComponentProps<typeof Button> & { label: string; icon?: React.ReactNode }) {
  const form = useFormContext()
  return (
    <form.Subscribe selector={(state) => state.isSubmitting}>
      {(isSubmitting) => (
        <Button type="submit" {...props} disabled={props.disabled ?? isSubmitting}>
          {isSubmitting && <Spinner />}
          {label}
          {icon}
        </Button>
      )}
    </form.Subscribe>
  )
}

// The dirty forms on the page, in the order they became dirty. Only the first one guards navigation, so leaving a
// page with several unsaved sections asks once, not once per section.
const dirtyForms = new Set<string>()
const dirtyListeners = new Set<() => void>()
const subscribeDirty = (listener: () => void) => {
  dirtyListeners.add(listener)
  return () => dirtyListeners.delete(listener)
}
const firstDirtyForm = () => dirtyForms.values().next().value
function setFormDirty(id: string, dirty: boolean) {
  if (dirty === dirtyForms.has(id)) return
  if (dirty) dirtyForms.add(id)
  else dirtyForms.delete(id)
  for (const listener of dirtyListeners) listener()
}

/**
 * Save and Discard for a settings section, after the worldcoo backoffice's: nothing while the form holds what it
 * loaded, both buttons once it is edited. Clicked outside the `<form>` (they sit in the section's header), so they
 * drive the form directly rather than submitting it. Save on an invalid form marks every field touched, and the
 * form's own scroll-to-error takes the reader to the first one. While dirty, leaving the page asks first, in the
 * app's confirm dialog (and the browser's own prompt on unload).
 */
function SaveDiscardButtons(props: { className?: string }) {
  const { t } = useLingui()
  const form = useFormContext()
  const id = useId()
  const [confirm, confirmDialog] = useConfirm()
  // TanStack's `isDefaultValue` is strict per field: a field that started `null` and was typed into and cleared holds
  // `''`, and read as an edit. Its clean verdict stands (it is the baseline a save's `reset` set); a "changed" one is
  // re-checked with the normalising comparison. One judgement drives both the buttons and the leave guard.
  const { isSubmitting, isDefaultValue, canSubmit } = useStore(form.store, (state) => ({
    isDefaultValue: state.isDefaultValue || sameFormValue(state.values, form.options.defaultValues),
    isSubmitting: state.isSubmitting,
    canSubmit: state.canSubmit,
  }))
  const guarding = useSyncExternalStore(subscribeDirty, firstDirtyForm, () => undefined) === id

  useEffect(() => {
    setFormDirty(id, !isDefaultValue)
    return () => setFormDirty(id, false)
  }, [id, isDefaultValue])

  useBlocker({
    shouldBlockFn: async () =>
      !(await confirm({
        title: t`Leave without saving?`,
        description: t`You have unsaved changes on this page. If you leave now, they are lost.`,
        confirmLabel: t`Leave`,
        cancelLabel: t`Keep editing`,
        variant: 'destructive',
      })),
    enableBeforeUnload: guarding,
    disabled: !guarding,
  })

  const save = async () => {
    if (!canSubmit) {
      for (const name of Object.keys(form.state.fieldMeta)) {
        form.setFieldMeta(name as never, (meta) => ({ ...meta, isTouched: true }))
      }
      toast.error(t`Please review the highlighted fields before saving.`)
    }
    await form.handleSubmit()
    // Saved: what is on screen is the new baseline, so the section is clean (and the buttons go) until it changes.
    if (form.state.isSubmitSuccessful) form.reset(form.state.values)
  }

  if (isDefaultValue) return confirmDialog

  return (
    <div className={cn('flex gap-2', props.className)}>
      <Button type="button" variant="secondary" size="sm" onClick={() => form.reset()}>
        {t`Discard`}
      </Button>
      <Button type="button" size="sm" onClick={() => void save()} disabled={isSubmitting}>
        {isSubmitting && <Spinner />}
        {t`Save`}
      </Button>
      {confirmDialog}
    </div>
  )
}

const { useAppForm: _useAppForm } = createFormHook({
  fieldComponents: {
    TextField,
    TextareaField,
    DateField,
    DateTimeField,
    ColorField,
    FileField,
    ImageField,
    ImageUploadField,
    SelectTextField,
    SelectNumberField,
    ComboboxTextField,
    ComboboxNumberField,
    MultiComboboxTextField,
    MultiComboboxPopoverTextField,
    MultiComboboxNumberField,
    SimpleSelectField,
    SimpleSelectNumberField,
    CountryField,
    ProvinceField,
    NativeCountryField,
    NativeProvinceField,
    SectorField,
    FunctionField,
    CheckboxField,
    SwitchField,
    PasswordField,
  },
  formComponents: {
    SubmitButton,
    SaveDiscardButtons,
  },
  fieldContext,
  formContext,
})

function useAppFormWithScrollToError(...args: Parameters<typeof _useAppForm>): ReturnType<typeof _useAppForm> {
  const form = _useAppForm(...args)

  const submissionAttempts = useStore(form.store, (state: { submissionAttempts: number }) => state.submissionAttempts)
  const prevAttempts = useRef(0)
  useEffect(() => {
    if (submissionAttempts > prevAttempts.current) {
      prevAttempts.current = submissionAttempts
      const id = setTimeout(() => {
        const firstError = document.querySelector<HTMLElement>('[aria-invalid="true"]')
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
          firstError.focus({ preventScroll: true })
        }
      }, 0)
      return () => clearTimeout(id)
    }
  }, [submissionAttempts])

  return form
}

export const useAppForm = useAppFormWithScrollToError as unknown as typeof _useAppForm
